/**
 * Elsette V2 — Entry Point
 * 
 * SEPARAÇÃO PAINEL / LOJA:
 * Este ficheiro detecta o tipo de rota e carrega o app correcto:
 * 
 * - Rotas da LOJA (/produto, /p, /carrinho, /checkout) → StoreApp
 *   Bundle mínimo, sem código admin, optimizado para clientes
 * 
 * - Rotas do PAINEL (/login, /dashboard, /admin) → PanelApp
 *   Dashboard completo, protegido por auth
 * 
 * - Rota raiz (/) → DomainLanding
 *   Detecta domínio e redireciona
 * 
 * Cada app é carregado de forma independente via React.lazy().
 * O browser NUNCA carrega código do painel quando está na loja, e vice-versa.
 */

import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, RouterProvider, useLocation } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import '@/App.css';

// Patch global do toast — respeita o toggle de notificações em TODAS as páginas
const NOTIF_KEY = 'elsette_notifications_enabled';
const _origSuccess = toast.success.bind(toast);
const _origInfo = toast.info.bind(toast);
const _origWarning = toast.warning.bind(toast);
const isEnabled = () => { try { const v = localStorage.getItem(NOTIF_KEY); return v === null || v === 'true'; } catch { return true; } };
toast.success = (...args) => isEnabled() ? _origSuccess(...args) : undefined;
toast.info = (...args) => isEnabled() ? _origInfo(...args) : undefined;
toast.warning = (...args) => isEnabled() ? _origWarning(...args) : undefined;
// toast.error NÃO é patcheado — erros sempre aparecem

// DomainLanding é leve — carregado imediatamente
import DomainLanding from './pages/DomainLanding';

// Apps carregados de forma independente (code splitting completo)
const StoreApp = lazy(() => import('./apps/StoreApp'));
const PanelApp = lazy(() => import('./apps/PanelApp'));

const AppLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
  </div>
);

/**
 * AppRouter — Decide qual app carregar baseado na rota e domínio
 */
const AppRouter = () => {
  const location = useLocation();
  const path = location.pathname;
  const hostname = window.location.hostname.toLowerCase();

  // Check if it's a subdomain store (e.g., teste123.elsette.com)
  const isSubdomainStore = hostname.endsWith('.elsette.com') && 
    hostname !== 'www.elsette.com' && 
    hostname !== 'elsette.com';

  // Check if it's a custom domain (not platform)
  const isPlatform = hostname === 'elsette.com' || 
    hostname === 'www.elsette.com' || 
    hostname === 'localhost' || 
    hostname === '127.0.0.1' || 
    hostname.endsWith('.emergentagent.com');

  // Rotas da LOJA (público)
  const isStoreRoute = 
    path.startsWith('/produto/') || 
    path.startsWith('/p/') || 
    path.startsWith('/carrinho') || 
    path.startsWith('/checkout/');

  // Rotas do PAINEL (privado)
  const isPanelRoute = 
    path.startsWith('/login') || 
    path.startsWith('/dashboard') || 
    path.startsWith('/admin/');

  // Custom domain with unknown path → treat as vitrine slug
  const isCustomDomainVitrine = !isPlatform && !isStoreRoute && !isPanelRoute && path !== '/' && path.length > 1;

  // If it's a subdomain or custom domain with store routes, load StoreApp
  if (isStoreRoute || isCustomDomainVitrine) {
    return (
      <ChunkErrorBoundary>
      <Suspense fallback={<AppLoader />}>
        <StoreApp />
      </Suspense>
      </ChunkErrorBoundary>
    );
  }

  if (isPanelRoute) {
    return (
      <ChunkErrorBoundary>
      <Suspense fallback={<AppLoader />}>
        <PanelApp />
      </Suspense>
      </ChunkErrorBoundary>
    );
  }

  // Rota raiz — detecta domínio e redireciona
  return <DomainLanding />;
};

// Data router é necessário para useBlocker, inclusive em navegação por ?view=.
const panelRouter = createBrowserRouter([{ path: '*', element: <AppRouter /> }]);

function App() {
  return (
    <>
      <RouterProvider router={panelRouter} />
      <Toaster 
        position="bottom-center" 
        richColors 
        visibleToasts={1} 
        duration={2000} 
        toastOptions={{ 
          className: 'snackbar-toast',
          style: { 
            padding: '8px 16px',
            fontSize: '13px',
            minHeight: 'auto',
            borderRadius: '8px',
            maxWidth: '360px',
          } 
        }} 
      />
    </>
  );
}

export default App;
